import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Building2, Truck, BadgeCheck, Send, Wrench } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Reveal, SectionHeading } from "@/components/motion";

const inputCls = "w-full border border-girder bg-steel px-4 py-3 text-sm text-slate-100 placeholder:text-slate-600 focus:border-hivis focus:outline-none";
const labelCls = "mb-1.5 block font-mono text-[11px] uppercase tracking-widest text-slate-500";

export default function CorporatePage() {
  const [form, setForm] = useState({ name: "", company: "", email: "", phone: "", brand: "", locations: 10, kit_interest: "two-post-kit", timeline: "30-60 days", message: "", hp: "" });
  const [sending, setSending] = useState(false);
  const [kits, setKits] = useState([]);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  useEffect(() => {
    api.get("/products").then((r) => setKits(r.data.filter((p) => p.category === "kit"))).catch(() => {});
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/corporate/quote", { ...form, locations: Number(form.locations) });
      toast.success("Rollout request received", { description: "Our team will reply within one business day." });
      setForm({ name: "", company: "", email: "", phone: "", brand: "", locations: 10, kit_interest: "two-post-kit", timeline: "30-60 days", message: "" });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setSending(false); }
  };

  return (
    <div data-testid="corporate-page">
      <PageHeader
        kicker="Corporate & Franchise Buyers"
        title="One frame standard. Every location."
        sub="Banner Bar rollouts for franchise systems, dealer networks and multi-location brands. Uniform frames drop-shipped to every address — and local grounds crews install each one in about 15 minutes, or your local sign company can handle installation."
        image="/assets/img/w-gallery-img-8273_1.jpg"
      />

      <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-12 lg:py-24">
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4" data-testid="corporate-benefits">
          {[
            { icon: Wrench, t: "Easy Install", d: "Local grounds crew can install each frame in about 15 minutes. Or contract with your local sign company to receive professional installation service." },
            { icon: Building2, t: "Brand Uniformity", d: "The same frame, the same clean black finish, the same professional banner presentation at every single location — no store improvising with rope and zip ties." },
            { icon: Truck, t: "Direct-to-Store Fulfillment", d: "Kits drop-ship direct to each franchise address, on your rollout schedule." },
            { icon: BadgeCheck, t: "Volume Economics", d: "Multi-location pricing tiers, replacement bungee programs, and a dedicated contact who knows your rollout by name." },
          ].map((b, i) => (
            <Reveal key={b.t} i={i} className="clip-corner border border-girder bg-steel/50 p-8">
              <b.icon className="h-7 w-7 text-hivis" />
              <h3 className="mt-4 font-display text-2xl font-bold uppercase text-slate-100">{b.t}</h3>
              <p className="mt-3 text-sm leading-relaxed text-slate-400">{b.d}</p>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="border-t border-girder bg-steel/40 py-16 lg:py-24">
        <div className="mx-auto grid max-w-7xl gap-12 px-4 sm:px-6 lg:grid-cols-2 lg:px-12">
          <div>
            <SectionHeading kicker="Rollout Quote" title="Tell us about your footprint" />
            <Reveal i={2}>
              <p className="mt-6 max-w-md text-sm leading-relaxed text-slate-400 md:text-base">
                Share your location count and timeline and we'll come back with kit recommendations, per-location pricing and a fulfillment plan — usually within one business day.
              </p>
              <div className="mt-6 flex flex-wrap gap-2" data-testid="per-location-chips">
                {kits.map((k) => (
                  <span key={k.slug} className="border border-girder bg-obsidian px-3 py-1.5 font-mono text-[10px] uppercase tracking-widest text-slate-400">
                    {k.name.replace(" Kit", "")} · ${Math.round(k.price / 100)}/location
                  </span>
                ))}
              </div>
              <p className="mt-3 max-w-md font-mono text-[10px] uppercase tracking-widest text-slate-600">Retail starting points — rollout quotes price below retail at volume</p>
            </Reveal>
            <Reveal i={3}>
              <div className="mt-8 clip-corner overflow-hidden border border-girder">
                <img src="/assets/img/w-v-config-toyota-truck-month.jpg" alt="V-Config Banner Bar frame with Truck Month banner at a Toyota dealership" loading="lazy" className="aspect-[16/9] w-full object-cover" />
              </div>
            </Reveal>
            <Reveal i={4}>
              <p className="mt-6 border border-girder bg-obsidian/60 px-5 py-4 text-sm text-slate-400">
                Buying for a city, school, or public organization?{" "}
                <Link to="/institutions" data-testid="corporate-institutions-link" className="whitespace-nowrap font-mono text-xs uppercase tracking-widest text-hivis underline decoration-hivis/50 underline-offset-4 hover:text-hivis-hover">Institutional quotes</Link>
              </p>
            </Reveal>
          </div>
          <Reveal i={1}>
            <form onSubmit={submit} className="clip-corner border border-girder bg-obsidian p-6 sm:p-8" data-testid="corporate-quote-form">
              {/* Honeypot: invisible to humans; bots that fill it are silently dropped server-side */}
              <input type="text" name="hp" value={form.hp} onChange={set("hp")} tabIndex={-1} autoComplete="off" aria-hidden="true"
                style={{ position: "absolute", left: "-9999px", width: "1px", height: "1px", opacity: 0 }} data-testid="hp-field" />
              <div className="grid gap-4 sm:grid-cols-2">
                <div><label className={labelCls} htmlFor="cq-name">Your name *</label><input id="cq-name" required value={form.name} onChange={set("name")} className={inputCls} data-testid="cq-name" placeholder="Jordan Smith" /></div>
                <div><label className={labelCls} htmlFor="cq-company">Company *</label><input id="cq-company" required value={form.company} onChange={set("company")} className={inputCls} data-testid="cq-company" placeholder="Franchise Corp" /></div>
                <div><label className={labelCls} htmlFor="cq-email">Work email *</label><input id="cq-email" required type="email" value={form.email} onChange={set("email")} className={inputCls} data-testid="cq-email" placeholder="jordan@brand.com" /></div>
                <div><label className={labelCls} htmlFor="cq-phone">Phone</label><input id="cq-phone" value={form.phone} onChange={set("phone")} className={inputCls} data-testid="cq-phone" placeholder="(555) 555-0100" /></div>
                <div><label className={labelCls} htmlFor="cq-brand">Franchise / brand</label><input id="cq-brand" value={form.brand} onChange={set("brand")} className={inputCls} data-testid="cq-brand" placeholder="Brand name" /></div>
                <div>
                  <label className={labelCls} htmlFor="cq-locations">Locations: <span className="text-hivis" data-testid="cq-locations-value">{form.locations}</span></label>
                  <input id="cq-locations" type="range" min="4" max="500" value={form.locations} onChange={set("locations")} className="w-full accent-amber-500" data-testid="cq-locations" />
                </div>
                <div>
                  <label className={labelCls} htmlFor="cq-kit">Kit interest</label>
                  <select id="cq-kit" value={form.kit_interest} onChange={set("kit_interest")} className={inputCls} data-testid="cq-kit">
                    <option value="small-form-kit">Small Form Kit (4 ft)</option>
                    <option value="two-post-kit">Two Post Kit (6-8 ft)</option>
                    <option value="three-post-kit">Three Post Kit (12-16 ft)</option>
                    <option value="v-config-kit">V-Config Kit (corner)</option>
                    <option value="mixed">Mixed / not sure yet</option>
                  </select>
                </div>
                <div>
                  <label className={labelCls} htmlFor="cq-timeline">Rollout timeline</label>
                  <select id="cq-timeline" value={form.timeline} onChange={set("timeline")} className={inputCls} data-testid="cq-timeline">
                    <option>ASAP</option><option>30-60 days</option><option>This quarter</option><option>Planning ahead</option>
                  </select>
                </div>
              </div>
              <div className="mt-4"><label className={labelCls} htmlFor="cq-message">Notes</label><textarea id="cq-message" rows={3} value={form.message} onChange={set("message")} className={inputCls} data-testid="cq-message" placeholder="Anything else we should know — banner sizes, regions, install support..." /></div>
              <button type="submit" disabled={sending} data-testid="corporate-quote-submit" className="clip-corner mt-6 flex w-full items-center justify-center gap-3 bg-hivis py-4 font-display text-lg font-bold uppercase tracking-wide text-obsidian transition-colors hover:bg-hivis-hover disabled:opacity-60">
                {sending ? "Sending..." : "Request Rollout Quote"} <Send className="h-4 w-4" />
              </button>
            </form>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
